import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, Clock, Trophy, Users, Target, BarChart3, Eye, MapPin, Shield, Hash, ArrowLeft } from 'lucide-react';
import { collection, onSnapshot, query, orderBy, where } from 'firebase/firestore';
import { db } from '../config/firebase';

const ResultsPage = () => {
  const [fixtures, setFixtures] = useState([]);
  const [teams, setTeams] = useState({});
  const [loading, setLoading] = useState(true);
  const [dateFilter, setDateFilter] = useState('all');
  const [customDateRange, setCustomDateRange] = useState({ start: '', end: '' });

  // Date filtering functions
  const getDateFilters = () => {
    const today = new Date();
    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - today.getDay());
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6);
    
    const startOfLastWeek = new Date(startOfWeek);
    startOfLastWeek.setDate(startOfWeek.getDate() - 7);
    const endOfLastWeek = new Date(startOfLastWeek);
    endOfLastWeek.setDate(startOfLastWeek.getDate() + 6);

    return {
      today: today.toISOString().split('T')[0],
      thisWeek: { start: startOfWeek.toISOString().split('T')[0], end: endOfWeek.toISOString().split('T')[0] },
      lastWeek: { start: startOfLastWeek.toISOString().split('T')[0], end: endOfLastWeek.toISOString().split('T')[0] }
    };
  };

  const filterFixturesByDate = (fixtures) => {
    if (dateFilter === 'all') return fixtures;
    
    const dateFilters = getDateFilters();
    
    return fixtures.filter(fixture => {
      const fixtureDate = fixture.date;
      
      switch (dateFilter) {
        case 'today':
          return fixtureDate === dateFilters.today;
        case 'thisWeek':
          return fixtureDate >= dateFilters.thisWeek.start && fixtureDate <= dateFilters.thisWeek.end;
        case 'lastWeek':
          return fixtureDate >= dateFilters.lastWeek.start && fixtureDate <= dateFilters.lastWeek.end;
        case 'custom':
          if (customDateRange.start && customDateRange.end) {
            return fixtureDate >= customDateRange.start && fixtureDate <= customDateRange.end;
          }
          return true;
        default:
          return true;
      }
    });
  };

  // Function to group fixtures by team pairing
  const groupFixturesByTeamPairing = (fixtures) => {
    const groups = {};
    
    fixtures.forEach(fixture => {
      const key = `${fixture.homeTeamId}-${fixture.awayTeamId}`;
      if (!groups[key]) {
        groups[key] = {
          homeTeam: fixture.homeTeam,
          awayTeam: fixture.awayTeam,
          homeTeamId: fixture.homeTeamId,
          awayTeamId: fixture.awayTeamId,
          round: fixture.round,
          date: fixture.date,
          court: fixture.court,
          matches: []
        };
      }
      groups[key].matches.push(fixture);
    });
    
    return Object.values(groups).sort((a, b) => {
      if (a.round !== b.round) return a.round - b.round;
      return a.homeTeam.localeCompare(b.homeTeam);
    });
  };

  useEffect(() => {
    let unsubscribeFixtures = null;
    let unsubscribeTeams = null;

    const setupListeners = () => {
      // Fetch only completed fixtures
      const fixturesQuery = query(collection(db, 'fixtures'));

      unsubscribeFixtures = onSnapshot(fixturesQuery, (snapshot) => {
        let fixturesData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        // Filter only completed fixtures
        fixturesData = fixturesData.filter(f => f.status === 'completed');
        
        // Apply date filtering
        fixturesData = filterFixturesByDate(fixturesData);
        
        // Sort by date (most recent first)
        fixturesData.sort((a, b) => new Date(b.date) - new Date(a.date));
        
        setFixtures(fixturesData);
        setLoading(false);
      });

      // Fetch teams for logos
      unsubscribeTeams = onSnapshot(collection(db, 'teams'), (snapshot) => {
        const teamsData = {};
        snapshot.docs.forEach(doc => {
          teamsData[doc.id] = doc.data();
        });
        setTeams(teamsData);
      });
    };

    setupListeners();

    return () => {
      if (unsubscribeFixtures) unsubscribeFixtures();
      if (unsubscribeTeams) unsubscribeTeams();
    };
  }, [dateFilter, customDateRange]);

  const formatSetScore = (set) => {
    if (set.set === 3) {
      // Super tiebreak - show as is
      return `${set.homeGames} - ${set.awayGames}`;
    }
    
    // Regular sets - check if it was a tiebreak
    if (set.homeGames >= 6 && set.awayGames >= 6) {
      // It was a tiebreak, show as 7-6 (7) or 6-7 (7)
      const winner = set.homeGames > set.awayGames ? 'home' : 'away';
      const tiebreakScore = Math.max(set.homeGames, set.awayGames);
      if (winner === 'home') {
        return `7 - 6 (${tiebreakScore})`;
      } else {
        return `6 - 7 (${tiebreakScore})`;
      }
    }
    
    // Regular set score
    return `${set.homeGames} - ${set.awayGames}`;
  };

  const getMatchWinner = (fixture) => {
    if (!fixture.sets) return null;
    
    const homeSetsWon = fixture.sets.filter(s => s.completed && s.homeScore > s.awayScore).length;
    const awaySetsWon = fixture.sets.filter(s => s.completed && s.awayScore > s.homeScore).length;
    
    // Check super tiebreak (set 3)
    const superTiebreak = fixture.sets[2];
    if (superTiebreak && superTiebreak.completed) {
      return superTiebreak.homeScore > superTiebreak.awayScore ? 'home' : 'away';
    }
    
    return homeSetsWon > awaySetsWon ? 'home' : 'away';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-gray-50">
      <div className="container mx-auto px-4 py-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex justify-between items-center mb-8"
        >
          <div>
            <div className="flex items-center gap-4 mb-2">
              <Link
                to="/fixtures"
                className="flex items-center gap-2 text-gray-600 hover:text-gray-800"
              >
                <ArrowLeft size={20} />
                Back to Fixtures
              </Link>
            </div>
            <h1 className="text-4xl font-bold mb-2 text-green-600">
              Match Results
            </h1>
            <p className="text-gray-600">
              View completed match results and statistics
            </p>
          </div>
        </motion.div>

        {/* Date Filters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-8"
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {/* Date Filters */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', backgroundColor: 'white', borderRadius: '0.75rem', padding: '0.5rem', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}>
              {[
                { key: 'all', label: 'All Dates', icon: Calendar },
                { key: 'today', label: 'Today', icon: Clock },
                { key: 'thisWeek', label: 'This Week', icon: Calendar },
                { key: 'lastWeek', label: 'Last Week', icon: Calendar },
                { key: 'custom', label: 'Custom', icon: Target }
              ].map(({ key, label, icon: Icon }) => (
                <button
                  key={key}
                  onClick={() => setDateFilter(key)}
                  style={{
                    flex: 1,
                    padding: '0.75rem 1.5rem',
                    borderRadius: '0.5rem',
                    fontSize: '0.875rem',
                    fontWeight: '700',
                    transition: 'all 0.3s',
                    border: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem',
                    minWidth: '120px',
                    background: dateFilter === key ? 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)' : 'transparent',
                    color: dateFilter === key ? 'white' : '#6B7280',
                    boxShadow: dateFilter === key ? '0 4px 12px rgba(59, 130, 246, 0.3)' : 'none'
                  }}
                >
                  <Icon size={16} />
                  {label}
                </button>
              ))}
            </div>

            {/* Custom Date Range */}
            {dateFilter === 'custom' && (
              <div style={{ display: 'flex', gap: '1rem', backgroundColor: 'white', borderRadius: '0.75rem', padding: '1rem', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', color: '#374151', marginBottom: '0.5rem' }}>
                    Start Date
                  </label>
                  <input
                    type="date"
                    value={customDateRange.start}
                    onChange={(e) => setCustomDateRange({ ...customDateRange, start: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '2px solid #E5E7EB',
                      borderRadius: '0.5rem',
                      fontSize: '0.875rem'
                    }}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', color: '#374151', marginBottom: '0.5rem' }}>
                    End Date
                  </label>
                  <input
                    type="date"
                    value={customDateRange.end}
                    onChange={(e) => setCustomDateRange({ ...customDateRange, end: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '2px solid #E5E7EB',
                      borderRadius: '0.5rem',
                      fontSize: '0.875rem'
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        </motion.div>

        {loading ? (
          <div className="flex justify-center items-center py-20">
            <div className="text-center">
              <Trophy size={48} className="mx-auto mb-4 text-green-600 animate-bounce" />
              <p className="text-gray-600">Loading results...</p>
            </div>
          </div>
        ) : fixtures.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            {groupFixturesByTeamPairing(fixtures).map((group, groupIndex) => (
              <div key={`group-${groupIndex}`} style={{ marginBottom: '2rem' }}>
                {/* Team Pairing Header */}
                <div style={{
                  background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                  color: 'white',
                  padding: '1rem 1.5rem',
                  borderRadius: '1rem 1rem 0 0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: '0.5rem'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                    <Trophy size={24} />
                    <div>
                      <h3 style={{ fontSize: '1.25rem', fontWeight: 'bold', margin: 0 }}>
                        {group.homeTeam} vs {group.awayTeam}
                      </h3>
                      <p style={{ fontSize: '0.875rem', margin: 0, opacity: 0.9 }}>
                        {group.matches.length} matches • {group.date} • {group.court}
                      </p>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span style={{ fontSize: '0.875rem', opacity: 0.9 }}>
                      Round {group.round}
                    </span>
                  </div>
                </div>
                
                {/* Individual Matches */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1rem' }}>
                  {group.matches.map((fixture, matchIndex) => {
                    const homeTeam = teams[fixture.homeTeamId];
                    const awayTeam = teams[fixture.awayTeamId];
                    const matchWinner = getMatchWinner(fixture);
                    
                    return (
                      <motion.div
                        key={fixture.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: matchIndex * 0.05 }}
                        whileHover={{ scale: 1.02, y: -2 }}
                        style={{
                          backgroundColor: 'white',
                          borderRadius: '1rem',
                          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                          overflow: 'hidden',
                          border: '1px solid rgba(0, 0, 0, 0.05)',
                          transition: 'all 0.3s'
                        }}
                      >
                        {/* Header with Status & Court */}
                        <div style={{
                          background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                          padding: '0.75rem 1.5rem',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between'
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'white', fontWeight: '700', fontSize: '0.875rem' }}>
                            <Trophy size={16} />
                            COMPLETED
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'rgba(255, 255, 255, 0.9)', fontSize: '0.875rem' }}>
                            <MapPin size={14} />
                            <span>{fixture.court || 'Court TBD'}</span>
                            {fixture.matchNumber && (
                              <>
                                <span style={{ margin: '0 0.25rem' }}>•</span>
                                <Hash size={14} />
                                <span>#{fixture.matchNumber}</span>
                              </>
                            )}
                          </div>
                        </div>
                        
                        {/* Teams Display */}
                        <div style={{ padding: 'clamp(1rem, 3vw, 1.5rem)' }}>
                          {/* Match Score Summary */}
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 'clamp(1rem, 3vw, 2rem)',
                            marginBottom: 'clamp(1rem, 2vw, 1.5rem)',
                            flexWrap: 'wrap'
                          }}>
                            {/* Home Team Column */}
                            <div style={{
                              flex: '1 1 0',
                              minWidth: '140px',
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              textAlign: 'center',
                              gap: '0.75rem'
                            }}>
                              {homeTeam?.logo ? (
                                <img 
                                  src={homeTeam.logo} 
                                  alt={fixture.homeTeam} 
                                  style={{
                                    width: 'clamp(60px, 12vw, 80px)',
                                    height: 'clamp(60px, 12vw, 80px)',
                                    borderRadius: '12px',
                                    objectFit: 'cover',
                                    boxShadow: matchWinner === 'home' ? '0 8px 20px rgba(5, 150, 105, 0.4)' : '0 4px 12px rgba(0, 0, 0, 0.15)',
                                    border: matchWinner === 'home' ? '3px solid #10B981' : '2px solid #E5E7EB'
                                  }}
                                  crossOrigin="anonymous"
                                />
                              ) : (
                                <div style={{
                                  width: 'clamp(60px, 12vw, 80px)',
                                  height: 'clamp(60px, 12vw, 80px)',
                                  borderRadius: '12px',
                                  background: 'linear-gradient(135deg, #D1FAE5 0%, #10B981 100%)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                                  border: matchWinner === 'home' ? '3px solid #10B981' : '2px solid #E5E7EB'
                                }}>
                                  <Users size={28} style={{ color: '#059669' }} />
                                </div>
                              )}
                              <div style={{ width: '100%' }}>
                                <div style={{ 
                                  fontSize: 'clamp(1rem, 2.5vw, 1.25rem)', 
                                  fontWeight: 'bold', 
                                  color: '#1F2937',
                                  marginBottom: '0.25rem',
                                  lineHeight: 1.2
                                }}>
                                  {fixture.homeTeam}
                                </div>
                                {matchWinner === 'home' && (
                                  <div style={{ 
                                    fontSize: 'clamp(0.75rem, 1.5vw, 0.875rem)', 
                                    color: '#059669', 
                                    fontWeight: '600',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '0.25rem',
                                    background: '#D1FAE5',
                                    padding: '0.25rem 0.75rem',
                                    borderRadius: '1rem'
                                  }}>
                                    <Trophy size={14} />
                                    Winner
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Score Display */}
                            <div style={{ 
                              display: 'flex', 
                              flexDirection: 'column', 
                              alignItems: 'center',
                              gap: '0.5rem',
                              minWidth: 'clamp(80px, 15vw, 120px)',
                              flexShrink: 0
                            }}>
                              {fixture.sets && (() => {
                                const homeSetsWon = fixture.sets.filter(s => s.completed && s.homeScore > s.awayScore).length;
                                const awaySetsWon = fixture.sets.filter(s => s.completed && s.awayScore > s.homeScore).length;
                                return (
                                  <>
                                    <div style={{ 
                                      fontSize: 'clamp(2rem, 5vw, 3rem)', 
                                      fontWeight: 'bold',
                                      color: '#1C563D',
                                      lineHeight: 1
                                    }}>
                                      {homeSetsWon} - {awaySetsWon}
                                    </div>
                                    <div style={{
                                      fontSize: 'clamp(0.625rem, 1.2vw, 0.75rem)',
                                      color: '#059669',
                                      textTransform: 'uppercase',
                                      fontWeight: '700',
                                      letterSpacing: '0.5px'
                                    }}>
                                      FINAL
                                    </div>
                                    <div style={{ 
                                      display: 'flex',
                                      flexDirection: 'column',
                                      gap: '0.25rem',
                                      fontSize: 'clamp(0.65rem, 1.2vw, 0.75rem)', 
                                      color: '#9CA3AF',
                                      textAlign: 'center'
                                    }}>
                                      {fixture.sets.map((set, index) => (
                                        <div key={index} style={{ fontWeight: '600' }}>
                                          Set {set.set}: {set.homeScore || set.homeGames || 0}-{set.awayScore || set.awayGames || 0}
                                        </div>
                                      ))}
                                    </div>
                                  </>
                                );
                              })()}
                            </div>

                            {/* Away Team Column */}
                            <div style={{
                              flex: '1 1 0',
                              minWidth: '140px',
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              textAlign: 'center',
                              gap: '0.75rem'
                            }}>
                              {awayTeam?.logo ? (
                                <img 
                                  src={awayTeam.logo} 
                                  alt={fixture.awayTeam} 
                                  style={{
                                    width: 'clamp(60px, 12vw, 80px)',
                                    height: 'clamp(60px, 12vw, 80px)',
                                    borderRadius: '12px',
                                    objectFit: 'cover',
                                    boxShadow: matchWinner === 'away' ? '0 8px 20px rgba(5, 150, 105, 0.4)' : '0 4px 12px rgba(0, 0, 0, 0.15)',
                                    border: matchWinner === 'away' ? '3px solid #10B981' : '2px solid #E5E7EB'
                                  }}
                                  crossOrigin="anonymous"
                                />
                              ) : (
                                <div style={{
                                  width: 'clamp(60px, 12vw, 80px)',
                                  height: 'clamp(60px, 12vw, 80px)',
                                  borderRadius: '12px',
                                  background: 'linear-gradient(135deg, #DBEAFE 0%, #3B82F6 100%)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                                  border: matchWinner === 'away' ? '3px solid #10B981' : '2px solid #E5E7EB'
                                }}>
                                  <Users size={28} style={{ color: '#2563EB' }} />
                                </div>
                              )}
                              <div style={{ width: '100%' }}>
                                <div style={{ 
                                  fontSize: 'clamp(1rem, 2.5vw, 1.25rem)', 
                                  fontWeight: 'bold', 
                                  color: '#1F2937',
                                  marginBottom: '0.25rem',
                                  lineHeight: 1.2
                                }}>
                                  {fixture.awayTeam}
                                </div>
                                {matchWinner === 'away' && (
                                  <div style={{ 
                                    fontSize: 'clamp(0.75rem, 1.5vw, 0.875rem)', 
                                    color: '#059669', 
                                    fontWeight: '600',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '0.25rem',
                                    background: '#D1FAE5',
                                    padding: '0.25rem 0.75rem',
                                    borderRadius: '1rem'
                                  }}>
                                    <Trophy size={14} />
                                    Winner
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Date & Time */}
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '1.5rem',
                            padding: '0.75rem',
                            background: 'linear-gradient(135deg, #F9FAFB 0%, #F3F4F6 100%)',
                            borderRadius: '0.5rem',
                            marginBottom: '1rem'
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#6B7280', fontSize: '0.875rem', fontWeight: '600' }}>
                              <Calendar size={16} />
                              <span>{new Date(fixture.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#6B7280', fontSize: '0.875rem', fontWeight: '600' }}>
                              <Clock size={16} />
                              <span>{fixture.time}</span>
                            </div>
                          </div>

                          {/* Actions */}
                          <div style={{ display: 'flex', gap: '0.5rem' }}>
                            <Link
                              to={`/fan/match/${fixture.id}`}
                              target="_blank"
                              className="btn btn-secondary"
                              style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', textDecoration: 'none' }}
                            >
                              <Eye size={16} />
                              View Match
                            </Link>
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            style={{ 
              textAlign: 'center', 
              padding: '4rem 2rem',
              background: 'white',
              borderRadius: '1rem',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
            }}
          >
            <Trophy size={64} style={{ color: '#D1D5DB', margin: '0 auto 1rem' }} />
            <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#6B7280', marginBottom: '0.5rem' }}>
              No Results Found
            </h3>
            <p style={{ color: '#9CA3AF' }}>
              No completed matches found for the selected date range.
            </p>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default ResultsPage;
